import React, { useId, useMemo, useState, useEffect } from "react";
import PersonalNameFields, { personalNameFields } from "@/components/PersonalNameFields";
import { Link } from "react-router-dom";
import {
  Calendar, CalendarClock,
  BedDouble, Sparkles, Send, Check,
  Sun, Bike, Camera, Flower, Music, Waves,
  Mountain, MountainSnow, MapPin, ArrowRight, Compass,
  Moon, ArrowUpRight, X,
} from "lucide-react";
import { useLanguage, pick } from "@/contexts/LanguageContext";
import { ALL_TRIPS, TRIP_REGIONS } from "@/lib/allTripsCatalog";
import EditableImage from "@/components/EditableImage";
import FromPrice from "@/components/FromPrice";
import EditableText from "@/components/EditableText";
import { SlotScope, useSlotId } from "@/components/slotScope";
import { pathFor } from "@/lib/routes";
import { resolveTripContext, getTripParams, setTripContext } from "@/lib/tripContext";
import { useLeadCapture } from "@/lib/leadCapture";
import { contactSubmissionFields, contactSubmissionError, DEFAULT_CONTACT_PREFERENCE } from "@/lib/contactSubmission";
import { optimizedSrc } from "@/lib/imageUrl";
import { WhatHappensNext, ContactPreference } from "@/components/FormExtras";
import InternationalPhoneInput, { isValidInternationalPhone } from "@/components/InternationalPhoneInput";
import LeadSubmissionSuccess from "@/components/LeadSubmissionSuccess";
import VoiceTextField, { VoiceDictationProvider } from "@/components/VoiceTextField";
import TripPlanningFields, { TRIP_PLANNING_COPY, PlanningField as Field, PlanningSection as SectionBlock, planningInputClass as inputCls } from "@/components/TripPlanningFields";

/* ============================================================
   PlannerForm · reusable detailed trip-planner form
   ------------------------------------------------------------
   Extracted from the old PlanificaTuViajePage so it can be
   embedded inside the tabbed <FormTabs> on both /planifica-tu-viaje
   and /contacto. Posts to backend POST /api/trip-planner.
   Slot ids stay page-namespaced via useSlotId so CMS edits on
   /planifica-tu-viaje are preserved unchanged.
============================================================ */

const T = (es, en, fr) => ({ es, en, fr });

export const PLANNER_COPY = {
  eyebrow: T("Planifica tu viaje", "Plan your trip", "Planifiez votre voyage"),
  title:   T(
    "Cuéntanos cómo soñaste con Marruecos.",
    "Tell us how you dreamed of Morocco.",
    "Dites-nous comment vous avez rêvé du Maroc.",
  ),
  intro: T(
    "Cuatro pasos y un mensaje opcional. Nuestro equipo en destino te responde con una propuesta personalizada en 24-48 h.",
    "Four short steps and an optional note. Our in-country team replies with a custom proposal within 24-48 h.",
    "Quatre étapes courtes et une note optionnelle. Notre équipe sur place vous répond avec une proposition sur mesure sous 24-48 h.",
  ),
  step:        T("Paso", "Step", "Étape"),
  ...TRIP_PLANNING_COPY,
  // Section 3
  s3_title:    T("Alojamiento", "Accommodation", "Hébergement"),
  s3_help:     T("Tres categorías curadas. Siempre con encanto local.", "Three curated categories — always with local charm.", "Trois catégories sélectionnées — toujours avec du charme local."),
  // Section 4 — Regions (geographic preferences)
  s4r_title:   T("Regiones que quieres visitar", "Regions you want to visit", "Régions que vous souhaitez visiter"),
  s4r_help:    T("Marca una o varias zonas. Usamos tu selección para sugerirte los itinerarios que mejor encajan.",
                 "Pick one or several zones. We use your choice to recommend itineraries that match.",
                 "Choisissez une ou plusieurs zones. Nous l'utilisons pour vous recommander les itinéraires adaptés."),
  s4r_recos:   T("Itinerarios sugeridos para ti", "Suggested itineraries for you", "Itinéraires suggérés pour vous"),
  s4r_help2:   T("Haz clic en un itinerario para añadirlo a tu planificación. Usa «Ver detalle» para abrir su página.",
                 "Click an itinerary to add it to your plan. Use \"View details\" to open its page.",
                 "Cliquez sur un itinéraire pour l'ajouter à votre plan. Utilisez « Voir le détail » pour ouvrir sa page."),
  s4r_match:   T("coinciden con tu selección", "match your selection", "correspondent à votre sélection"),
  s4r_see:     T("Ver itinerario", "View itinerary", "Voir l'itinéraire"),
  s4r_detail:  T("Ver detalle", "View details", "Voir le détail"),
  s4r_select:  T("Seleccionar", "Select", "Sélectionner"),
  s4r_selected: T("Añadido", "Added", "Ajouté"),
  s4r_selcount: T("itinerarios en tu planificación", "itineraries in your plan", "itinéraires dans votre plan"),
  s4r_seeall:  T("Ver todos los viajes", "See all trips", "Voir tous les voyages"),
  s4r_nights:  T("noches", "nights", "nuits"),
  // Section 5 (was 4 — Activities)
  s4_title:    T("Actividades", "Activities", "Activités"),
  s4_help:     T("Marca todo lo que te apetezca; nada es obligatorio.", "Tick anything that calls you; nothing is mandatory.", "Cochez ce qui vous attire ; rien n'est obligatoire."),
  // Section 5
  s5_title:    T("Tus datos", "Your details", "Vos coordonnées"),
  s5_help:     T("Solo te escribimos para preparar tu propuesta.", "We will only write to prepare your proposal.", "Nous vous écrivons uniquement pour préparer votre proposition."),
  email:       T("Email", "Email", "Email"),
  phone:       T("Teléfono", "Phone", "Téléphone"),
  notes:       T("Comentarios o ideas", "Notes or ideas", "Commentaires ou idées"),
  // Submit
  submit:      T("Enviar mi solicitud", "Send my request", "Envoyer ma demande"),
  sending:     T("Enviando…", "Sending…", "Envoi…"),
  submit_error: T("No hemos podido enviar la solicitud. Revisa tus datos e inténtalo de nuevo.", "We could not send your request. Please check your details and try again.", "Nous n’avons pas pu envoyer votre demande. Vérifiez vos coordonnées et réessayez."),
  success_t:   T("¡Recibido! Te respondemos en 24-48 h.", "Got it! We will reply within 24-48 h.", "Reçu ! Nous répondons sous 24-48 h."),
  success_b:   T(
    "Mientras tanto, explora nuestros viajes a medida o reserva una cita previa con el equipo.",
    "Meanwhile, explore our tailor-made tours or book an appointment with the team.",
    "En attendant, explorez nos voyages sur mesure ou prenez rendez-vous avec l'équipe.",
  ),
  required:    T("Campo obligatorio", "Required field", "Champ obligatoire"),
  privacy:     T(
    "No compartimos tus datos. Solo se usan para preparar tu propuesta.",
    "We never share your data. Only used to prepare your proposal.",
    "Nous ne partageons jamais vos données. Utilisées uniquement pour votre proposition.",
  ),
  send_back:   T("Volver al inicio", "Back to home", "Retour à l'accueil"),
};

const COPY = PLANNER_COPY;

const ACCOMMODATIONS = [
  {
    id: "basic", accent: "#A07042",
    title: T("Básico · auténtico", "Basic · authentic", "Basique · authentique"),
    desc:  T("Riads familiares, hostales con encanto, posadas bereberes.", "Family-run riads, charming inns, Berber guesthouses.", "Riads familiaux, auberges de charme, gîtes berbères."),
  },
  {
    id: "superior", accent: "#C16542",
    title: T("Superior", "Superior", "Supérieur"),
    desc:  T("Riads boutique 4★, kasbahs restauradas con piscina.", "Boutique 4★ riads, restored kasbahs with pool.", "Riads boutique 4★, kasbahs restaurées avec piscine."),
  },
  {
    id: "premium", accent: "#3A4A5F",
    title: T("Premium · 5★", "Premium · 5★", "Premium · 5★"),
    desc:  T("Hoteles 5★, riads de lujo, bivouacs premium en el desierto.", "5★ hotels, luxury riads, premium desert bivouacs.", "Hôtels 5★, riads de luxe, bivouacs premium dans le désert."),
  },
];

const ACTIVITIES = [
  { id: "camel",    icon: "mountain-snow", label: T("Paseo en camello", "Camel ride", "Balade à dos de dromadaire") },
  { id: "quad",     icon: "bike",          label: T("Quad por las dunas", "Quad in the dunes", "Quad dans les dunes") },
  { id: "hammam",   icon: "flower",        label: T("Hammam tradicional", "Traditional hammam", "Hammam traditionnel") },
  { id: "massage",  icon: "sparkles",      label: T("Masaje argán", "Argan oil massage", "Massage à l'argan") },
  { id: "cooking",  icon: "sun",           label: T("Clase de cocina marroquí", "Moroccan cooking class", "Cours de cuisine marocaine") },
  { id: "trekking", icon: "mountain",      label: T("Trekking en el Atlas", "Atlas trekking", "Trekking dans l'Atlas") },
  { id: "balloon",  icon: "camera",        label: T("Globo sobre Marrakech", "Hot-air balloon · Marrakech", "Montgolfière · Marrakech") },
  { id: "surf",     icon: "waves",         label: T("Surf en la costa atlántica", "Surf on the Atlantic coast", "Surf sur la côte atlantique") },
  { id: "music",    icon: "music",         label: T("Noche gnaoua en bivouac", "Gnawa night at the bivouac", "Soirée gnaoua au bivouac") },
];

const ICONS = { sun: Sun, bike: Bike, camera: Camera, flower: Flower, music: Music, waves: Waves, mountain: Mountain, "mountain-snow": MountainSnow, sparkles: Sparkles };

const REGION_ICONS = {
  sur: Sun, norte: MapPin, completo: Compass,
  escapadas: CalendarClock, aventura: Bike, eventos: Calendar,
};
const REGION_DESC = {
  sur:       T("Atlas, valles de kasbahs y dunas del Erg Chebbi.", "Atlas, kasbah valleys and Erg Chebbi dunes.", "Atlas, vallées des kasbahs et dunes de l'Erg Chebbi."),
  norte:     T("Ciudades imperiales, Rif y la costa del estrecho.", "Imperial cities, the Rif and the strait coast.", "Cités impériales, Rif et côte du détroit."),
  completo:  T("De Tánger o Fez hasta Marrakech y el desierto.", "From Tangier or Fez down to Marrakech and the desert.", "De Tanger ou Fès jusqu'à Marrakech et le désert."),
  escapadas: T("Estancias cortas centradas en una sola zona.", "Short stays focused on a single zone.", "Séjours courts centrés sur une seule zone."),
  aventura:  T("Rutas activas: enduro y pistas del Drâa.", "Active routes: enduro and Drâa tracks.", "Itinéraires actifs : enduro et pistes du Drâa."),
  eventos:   T("Salidas especiales en fechas señaladas.", "Special departures on key dates.", "Départs spéciaux à des dates clés."),
};
const REGION_OPTIONS = TRIP_REGIONS
  .filter((r) => r.id !== "all")
  .map((r) => ({ ...r, icon: REGION_ICONS[r.id] || Compass, desc: REGION_DESC[r.id] }));
const REGION_COUNTS = ALL_TRIPS.reduce((acc, t) => {
  acc[t.region] = (acc[t.region] || 0) + 1;
  return acc;
}, {});


/* Inline-CMS per-page text editor (auto-namespaced by page path). */
const ET = ({ k, defaults, as = "span", className, multiline = true, ...rest }) => {
  const slot = useSlotId(k);
  return <EditableText slot={slot} defaults={defaults || (k ? COPY[k] : {}) || {}} as={as} className={className} multiline={multiline} {...rest} />;
};

/* ============================================================
   PlannerForm — the detailed multi-step form (no hero).
============================================================ */
export default function PlannerForm() {
  const leadCapture = useLeadCapture("detailed_planning");
  const { lang } = useLanguage();
  const tr = (k) => pick(COPY[k], lang);

  const initialTrips = useMemo(() => {
    const resolved = getTripParams().map((id) => resolveTripContext(id, lang)).filter(Boolean);
    return {
      routeIds: resolved.map((t) => t.routeId),
      regions: [...new Set(resolved.map((t) => t.region).filter(Boolean))],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PREFILL_LABEL = { es: "Estás planificando", en: "You're planning", fr: "Vous planifiez" };
  const REVIEW_LABEL = { es: "Ver el itinerario", en: "View the itinerary", fr: "Voir l'itinéraire" };
  const DISMISS_LABEL = { es: "Quitar viaje seleccionado", en: "Remove selected trip", fr: "Retirer le voyage sélectionné" };

  const [form, setForm] = useState({
    dateMode: "range",
    startDate: "", endDate: "", exactDate: "", flexMonth: "",
    adults: 2, children: 0,
    accommodation: "superior",
    regions: initialTrips.regions,
    selectedTrips: initialTrips.routeIds,
    activities: [],
    first_name: "", last_name: "", email: "", phone: "", notes: "",
    preferredContact: DEFAULT_CONTACT_PREFERENCE,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [dictating, setDictating] = useState(false);
  const voiceId = useId();
  const [errMsg, setErrMsg] = useState("");

  // Persist the live trip selection for the whole session so it stays
  // pre-selected across navigation. Manual add/remove updates it too — a
  // manual change (or removal) always wins over the arrival context.
  useEffect(() => {
    setTripContext(form.selectedTrips);
  }, [form.selectedTrips]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArrayItem = (key) => (id) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
    }));
  const toggleRegion   = toggleArrayItem("regions");
  const toggleActivity = toggleArrayItem("activities");
  const toggleTrip     = toggleArrayItem("selectedTrips");

  // Banner reflects the LIVE selection: every selected itinerary,
  // resolved (catalog + program registry) and updated on add/remove.
  const selectedTripsCtx = useMemo(
    () => form.selectedTrips.map((id) => resolveTripContext(id, lang)).filter(Boolean),
    [form.selectedTrips, lang],
  );
  const removeTrip = (id) =>
    setForm((f) => ({ ...f, selectedTrips: f.selectedTrips.filter((x) => x !== id) }));

  const validate = () => {
    const e = {};
    for (const [key, value] of Object.entries(personalNameFields(form))) if (!value) e[key] = tr("required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = tr("required");
    if (!isValidInternationalPhone(form.phone)) e.phone = tr("required");
    if (!form.preferredContact.length) e.preferredContact = tr("required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (status === "sending" || dictating) return;
    if (!validate()) return;
    setStatus("sending");
    setErrMsg("");
    try {
      const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/trip-planner`;
      const payload = {
        ...leadCapture(),
        ...personalNameFields(form),
        ...contactSubmissionFields({
          email: form.email, phone: form.phone, preferred_contact: form.preferredContact,
        }),
        date_mode: form.dateMode,
        start_date: form.dateMode === "range"   ? (form.startDate || null) : (form.dateMode === "exact" ? (form.exactDate || null) : null),
        end_date:   form.dateMode === "range"   ? (form.endDate   || null) : null,
        flexible_month: form.dateMode === "flexible" ? (form.flexMonth || null) : null,
        travellers_adults: Number(form.adults) || 1,
        travellers_children: Number(form.children) || 0,
        accommodation: form.accommodation,
        regions: form.regions,
        selected_trips: form.selectedTrips,
        selected_trips_detail: form.selectedTrips.map((id) => {
          const t = resolveTripContext(id, lang);
          return {
            id,
            title: t ? t.title : id,
            url: `${window.location.origin}${pathFor(lang, id)}`,
            image: t && t.image ? t.image : undefined,
          };
        }),
        activities: form.activities,
        notes: form.notes.trim() || null,
        language: lang,
      };
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(contactSubmissionError(data.detail, tr("submit_error")));
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
      setErrMsg(err.message || "No se pudo confirmar el envío por correo.");
    }
  };

  const recommendedTrips = useMemo(() => {
    if (!form.regions.length) return [];
    return ALL_TRIPS.filter((t) => form.regions.includes(t.region));
  }, [form.regions]);

  return (
    <div data-testid="plan-trip-form-wrap" className="max-w-4xl mx-auto px-6 md:px-12">
      {status === "success" ? (
        <div
          data-testid="plan-trip-success"
          className="relative bg-white border border-[#2C2621]/10 p-10 md:p-14 text-center"
          style={{ borderTopColor: "#5A6B4F", borderTopWidth: 3 }}
        >
          <LeadSubmissionSuccess />
        </div>
      ) : (
        <VoiceDictationProvider onBusyChange={setDictating}>
        <form
          onSubmit={onSubmit}
          data-testid="plan-trip-form"
          className="space-y-16"
          noValidate
        >
          {/* Selection banner — live list of every selected itinerary.
              Stays in sync with the "Suggested itineraries" picker. */}
          {selectedTripsCtx.length > 0 && (
            <div
              data-testid="plan-prefill-banner"
              className="bg-[#F8F2E6] border border-[#C16542]/25 px-3 py-4 md:px-4 md:py-5"
              style={{ borderLeft: "3px solid #C16542" }}
            >
              <div className="flex items-center justify-between gap-3 px-2 mb-3">
                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C16542]">
                  <Compass className="w-3.5 h-3.5" strokeWidth={1.7} />
                  {pick(PREFILL_LABEL, lang)}
                </span>
                <span
                  data-testid="plan-prefill-count"
                  className="text-[10px] tracking-[0.28em] uppercase text-[#A07042]"
                >
                  {selectedTripsCtx.length}
                </span>
              </div>
              <ul className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                {selectedTripsCtx.map((t) => (
                  <li
                    key={t.routeId}
                    data-testid={`plan-prefill-item-${t.routeId}`}
                    className="flex items-stretch border border-[#2C2621]/10 bg-[#FDFBF7]/50"
                  >
                    <Link
                      to={pathFor(lang, t.routeId)}
                      data-testid={`plan-prefill-link-${t.routeId}`}
                      className="group flex items-center gap-3 flex-1 min-w-0 px-3 py-2.5 md:px-4 hover:bg-[#F2EBE1] transition-colors"
                    >
                      {t.image && (
                        <img src={optimizedSrc(t.image, 128)} alt="" loading="lazy" decoding="async" className="hidden sm:block w-12 h-12 object-cover shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-serif-x text-[15px] md:text-base text-[#2C2621] leading-snug truncate">
                          <span className="align-middle">{t.title}</span>
                          {t.durationLabel && (
                            <span className="text-[#5C5248] text-xs md:text-sm align-middle">{" · "}{t.durationLabel}</span>
                          )}
                        </p>
                        <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.16em] uppercase text-[#C16542] opacity-80 group-hover:opacity-100 transition-opacity">
                          {pick(REVIEW_LABEL, lang)}
                          <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.8} />
                        </span>
                      </div>
                    </Link>
                    <button
                      type="button"
                      onClick={() => removeTrip(t.routeId)}
                      data-testid={`plan-prefill-remove-${t.routeId}`}
                      aria-label={pick(DISMISS_LABEL, lang)}
                      title={pick(DISMISS_LABEL, lang)}
                      className="shrink-0 self-center mr-1.5 inline-flex items-center justify-center w-8 h-8 rounded-full text-[#5C5248] hover:bg-[#2C2621] hover:text-[#FDFBF7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542] transition-colors"
                    >
                      <X className="w-4 h-4" strokeWidth={1.8} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <TripPlanningFields value={form} onChange={set} lang={lang} monthDisabled={status === "sending"} renderText={key => <ET k={key} multiline={key.endsWith("_help")} />} />

          {/* ============ STEP 3 · ACCOMMODATION ============ */}
          <SectionBlock
            step="03" icon={BedDouble}
            title={<ET k="s3_title" multiline={false} />} help={<ET k="s3_help" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {ACCOMMODATIONS.map((a) => {
                const on = form.accommodation === a.id;
                return (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => set("accommodation", a.id)}
                    data-testid={`accommodation-${a.id}`}
                    className={`text-left p-6 border transition-all duration-300 relative ${
                      on
                        ? "bg-white border-transparent shadow-[0_15px_40px_-20px_rgba(44,38,33,0.35)]"
                        : "bg-white border-[#2C2621]/12 hover:border-[#2C2621]/40"
                    }`}
                    style={on ? { borderTopColor: a.accent, borderTopWidth: 3 } : undefined}
                  >
                    <ET k={`accommodation.${a.id}.title`} defaults={a.title} multiline={false} className="text-[10px] tracking-[0.3em] uppercase" style={{ color: a.accent }} />
                    <ET k={`accommodation.${a.id}.desc`} as="p" defaults={a.desc} className="mt-3 text-sm text-[#5C5248] leading-[1.7] min-h-[3.2rem]" />
                    {on && (
                      <span className="absolute top-4 right-4 inline-flex items-center justify-center w-6 h-6 rounded-full text-white" style={{ background: a.accent }}>
                        <Check className="w-3.5 h-3.5" strokeWidth={2} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </SectionBlock>

          {/* ============ STEP 4 · REGIONS ============ */}
          <SectionBlock
            step="04" icon={Compass}
            title={<ET k="s4r_title" multiline={false} />} help={<ET k="s4r_help" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="plan-regions">
              {REGION_OPTIONS.map((reg) => {
                const I = reg.icon;
                const on = form.regions.includes(reg.id);
                const count = REGION_COUNTS[reg.id] || 0;
                return (
                  <button
                    type="button"
                    key={reg.id}
                    onClick={() => toggleRegion(reg.id)}
                    data-testid={`region-${reg.id}`}
                    aria-pressed={on}
                    className={`group text-left p-5 border transition-all duration-300 relative ${
                      on
                        ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]"
                        : "bg-white text-[#3D352C] border-[#2C2621]/12 hover:border-[#2C2621]/40"
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      {I && <I className="w-4 h-4 shrink-0" strokeWidth={1.6} style={{ color: on ? "#D4A373" : "#C16542" }} />}
                      <ET k={`region.${reg.id}.label`} defaults={reg.label} multiline={false} className="text-[12px] tracking-[0.18em] uppercase flex-1" />
                      {on && <Check className="w-4 h-4 text-[#D4A373]" strokeWidth={2.2} />}
                    </span>
                    <ET k={`region.${reg.id}.desc`} as="p" defaults={reg.desc} className={`mt-2.5 text-[12px] leading-[1.6] ${on ? "text-[#FDFBF7]/75" : "text-[#5C5248]"}`} />
                    <span className={`mt-3 inline-block text-[10px] tracking-[0.25em] uppercase ${on ? "text-[#D4A373]" : "text-[#A07042]"}`}>
                      {count} · {tr("s4r_nights") === "noches" ? (count === 1 ? "itinerario" : "itinerarios") : count === 1 ? "itinerary" : "itineraries"}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Live recommendations */}
            <div className="mt-10" data-testid="plan-trip-recos">
              {form.regions.length === 0 ? (
                <p className="text-[13px] text-[#5C5248]/80 italic flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#C16542]" strokeWidth={1.6} />
                  <ET k="s4r_help" />
                </p>
              ) : (
                <>
                  <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
                    <ET k="s4r_recos" as="h3" multiline={false} className="font-serif-x text-xl md:text-2xl tracking-tight text-[#2C2621]" />
                    <span data-testid="plan-trip-recos-count" className="text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
                      {recommendedTrips.length} · <ET k="s4r_match" multiline={false} />
                    </span>
                  </div>
                  <ET k="s4r_help2" as="p" className="text-[12px] text-[#5C5248]/80 leading-relaxed mb-5 pb-5 border-b border-[#2C2621]/10 max-w-2xl" />
                  {form.selectedTrips.length > 0 && (
                    <p
                      data-testid="plan-trip-selected-count"
                      className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-[#2C2621] text-[#FDFBF7] text-[10px] tracking-[0.28em] uppercase"
                    >
                      <Check className="w-3.5 h-3.5 text-[#D4A373]" strokeWidth={2.2} />
                      {form.selectedTrips.length} · <ET k="s4r_selcount" multiline={false} />
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {recommendedTrips.map((trip) => (
                      <RecoCard
                        key={trip.routeId}
                        trip={trip}
                        lang={lang}
                        tr={tr}
                        selected={form.selectedTrips.includes(trip.routeId)}
                        onToggle={() => toggleTrip(trip.routeId)}
                      />
                    ))}
                  </div>
                  <Link
                    to={pathFor(lang, "toursLanding")}
                    data-testid="plan-trip-recos-seeall"
                    className="xaluca-button inline-flex items-center gap-2 mt-8 text-[11px] tracking-[0.28em] uppercase text-[#2C2621] hover:text-[#C16542] transition-colors"
                  >
                    <ET k="s4r_seeall" multiline={false} />
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.7} />
                  </Link>
                </>
              )}
            </div>
          </SectionBlock>

          {/* ============ STEP 5 · ACTIVITIES ============ */}
          <SectionBlock
            step="05" icon={Sparkles}
            title={<ET k="s4_title" multiline={false} />} help={<ET k="s4_help" />}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {ACTIVITIES.map((act) => {
                const I = ICONS[act.icon];
                const on = form.activities.includes(act.id);
                return (
                  <button
                    type="button"
                    key={act.id}
                    onClick={() => toggleActivity(act.id)}
                    data-testid={`activity-${act.id}`}
                    aria-pressed={on}
                    className={`group flex items-center gap-3 px-4 py-3.5 text-left text-[13px] border transition-all ${
                      on
                        ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]"
                        : "bg-white text-[#3D352C] border-[#2C2621]/12 hover:border-[#2C2621]/40"
                    }`}
                  >
                    {I && <I className="w-4 h-4 shrink-0" strokeWidth={1.6} style={{ color: on ? "#D4A373" : "#C16542" }} />}
                    <ET k={`activity.${act.id}`} defaults={act.label} multiline={false} className="flex-1 leading-tight" />
                    {on && <Check className="w-3.5 h-3.5 text-[#D4A373]" strokeWidth={2.2} />}
                  </button>
                );
              })}
            </div>
          </SectionBlock>

          {/* ============ STEP 6 · CONTACT ============ */}
          <SectionBlock
            step="06" icon={Send}
            title={<ET k="s5_title" multiline={false} />} help={<ET k="s5_help" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
              <PersonalNameFields value={form} onChange={set} lang={lang} errors={errors} disabled={status === "sending"}
                inputClass={inputCls} labelClass="text-[10px] uppercase tracking-[0.22em] text-[#5C5248]" testIdPrefix="planner" />
              <Field label={<ET k="email" multiline={false} />} required error={errors.email}>
                <input
                  type="email"
                  data-testid="email"
                  required
                  aria-required="true"
                  autoComplete="email"
                  maxLength={120}
                  className={inputCls}
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </Field>
              <Field as="div" label={tr("phone")} required error={errors.phone}>
                <InternationalPhoneInput
                  required
                  name="phone"
                  value={form.phone}
                  onValueChange={(phone) => {
                    set("phone", phone);
                    setErrors((current) => ({ ...current, phone: "" }));
                  }}
                  lang={lang}
                  invalid={Boolean(errors.phone)}
                  testId="phone"
                />
              </Field>
            </div>

            <div className="mt-8">
              <Field as="div" inputId={`${voiceId}-notes`} label={<ET k="notes" multiline={false} />}>
                <VoiceTextField as="textarea" id={`${voiceId}-notes`} aria-label={tr("notes")} lang={lang} disabled={status === "sending"}
                  data-testid="notes"
                  rows={4}
                  maxLength={3000}
                  className={`${inputCls} resize-none`}
                  value={form.notes}
                  onValueChange={value => set("notes", value)}
                />
              </Field>
            </div>

            <div className="mt-10">
              <ContactPreference
                tone="light"
                lang={lang}
                value={form.preferredContact}
                onChange={(value) => set("preferredContact", value)}
                error={errors.preferredContact}
                testidPrefix="planner-pref"
              />
            </div>
          </SectionBlock>

          {/* ============ WHAT HAPPENS NEXT ============ */}
          <div className="max-w-2xl">
            <WhatHappensNext tone="light" lang={lang} testid="planner-what-next" />
          </div>

          {/* ============ SUBMIT ============ */}
          <div className="pt-2">
            {status === "error" && (
              <p className="mb-5 text-sm text-[#C16542]" role="alert">{errMsg}</p>
            )}
            <button
              type="submit"
              data-testid="plan-trip-submit"
              disabled={status === "sending" || dictating}
              className="group relative inline-flex items-center gap-3 px-9 py-5 bg-[#C16542] text-[#FDFBF7] text-[11px] tracking-[0.3em] uppercase hover:bg-[#2C2621] transition-all duration-300 disabled:opacity-60"
            >
              {status === "sending" ? tr("sending") : <ET k="submit" multiline={false} />}
              <Send className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.6} />
            </button>
            <ET k="privacy" as="p" className="mt-5 text-xs text-[#5C5248]/70 max-w-xl" />
          </div>
        </form>
        </VoiceDictationProvider>
      )}
    </div>
  );
}

/* ============================================================
   RecoCard — Itinerary recommendation card shown in the region step.
============================================================ */
function RecoCard({ trip, lang, tr, selected, onToggle }) {
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };
  return (
    <SlotScope id={`plan-recos.${trip.routeId}`}>
      <div
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        onClick={onToggle}
        onKeyDown={onKeyDown}
        data-testid={`plan-trip-reco-${trip.routeId}`}
        className={`group relative flex flex-col cursor-pointer bg-white border transition-all duration-300 overflow-hidden hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(44,38,33,0.35)] ${
          selected
            ? "border-[#C16542] ring-2 ring-[#C16542]/40"
            : "border-[#2C2621]/10 hover:border-[#C16542]/60"
        }`}
      >
        <div className="relative w-full aspect-[4/3] overflow-hidden bg-[#1A1513]">
          <EditableImage
            slot={`plan-recos.${trip.routeId}.cover`}
            fallback={trip.image}
            alt={pick(trip.title, lang)}
            aspectRatio="4/3"
            imgProps={{ loading: "lazy" }}
            className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06] ${selected ? "scale-[1.03]" : ""}`}
          />
          <span
            className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-[9px] tracking-[0.26em] uppercase transition-all ${
              selected
                ? "bg-[#C16542] text-[#FDFBF7]"
                : "bg-[#FDFBF7]/95 text-[#2C2621] opacity-0 group-hover:opacity-100"
            }`}
          >
            <Check className="w-3 h-3" strokeWidth={2.4} />
            {selected ? tr("s4r_selected") : tr("s4r_select")}
          </span>
        </div>
        <div className="flex flex-col flex-1 p-4">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C16542] mb-1.5">
            <Moon className="w-3 h-3" strokeWidth={1.7} />
            {trip.nights} {tr("s4r_nights")}
          </div>
          <h4 className="font-serif text-[17px] text-[#2C2621] leading-snug mb-1.5">
            <ET k="title" defaults={trip.title} multiline={false} />
          </h4>
          <ET k="summary" as="p" defaults={trip.summary} className="text-[12px] text-[#5C5248] leading-relaxed flex-1" />
          <div className="mt-3">
            <FromPrice tone="dark" size="xs" testid={`plan-trip-reco-from-${trip.routeId}`} />
          </div>
          <Link
            to={pathFor(lang, trip.routeId)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`plan-trip-reco-detail-${trip.routeId}`}
            className="xaluca-button mt-4 inline-flex items-center gap-1.5 self-start text-[10px] tracking-[0.26em] uppercase text-[#2C2621] hover:text-[#C16542] border-b border-[#2C2621]/25 hover:border-[#C16542] pb-0.5 transition-colors"
          >
            {tr("s4r_detail")}
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.8} />
          </Link>
        </div>
      </div>
    </SlotScope>
  );
}
